import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTeamColors } from 'hooks';
import SEO from 'components/SEO/SEO';
import { BackIcon } from 'assets/icons';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 2rem;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 6rem;
  font-weight: bold;
  margin: 0;
  color: ${({ theme, $color }) =>
    $color || theme?.colors?.primary || '#ff3366'};
`;

const Subtitle = styled.h2`
  font-size: 2rem;
  margin: 1rem 0;
  color: ${({ theme }) => theme?.colors?.text || '#ffffff'};
`;

const Description = styled.p`
  font-size: 1.2rem;
  margin: 1rem 0 2rem;
  color: ${({ theme }) => theme?.colors?.textSecondary || '#cccccc'};
  max-width: 600px;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: white;
  background-color: ${({ theme, $bgColor }) =>
    $bgColor || theme?.colors?.primary || '#ff3366'};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

/**
 * 404 Not Found page component
 * @component
 * @returns {React.ReactElement} The 404 error page
 */
const NotFound = () => {
  const navigate = useNavigate();
  const { mainColor } = useTeamColors();

  useEffect(() => {
    // Set proper HTTP status for SEO (this helps with server-side rendering)
    document.title = '404 - Page Not Found | Downkount';
  }, []);

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <>
      <SEO
        description="Page not found. The page you're looking for doesn't exist or has been moved."
        noindex={true}
        title="404 - Page Not Found"
      />
      <Container>
        <Title $color={mainColor}>404</Title>
        <Subtitle>Page Not Found</Subtitle>
        <Description>
          Sorry, the page you're looking for doesn't exist. It may have been
          moved or deleted.
        </Description>
        <Button $bgColor={mainColor} onClick={handleGoHome}>
          <BackIcon color="white" />
          Go to Homepage
        </Button>
      </Container>
    </>
  );
};

export default NotFound;
